import { Close, DescriptionOutlined, FileDownloadOutlined, VerifiedOutlined } from '@mui/icons-material';
import {
    Box,
    DialogTitle,
    Divider,
    Button,
    Chip,
    Slide,
    CircularProgress,
    IconButton,
    Drawer,
    Grid,
    List,
    ListItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogActions,
    DialogContent,
    Tabs,
    Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { GetRootCauseAnalysisUploadForInstitution, GetRootCauseAnalysisUploadForInstitutionById, ValidateReturn } from '../../../redux/api/cases';
import PageContainer from '../../../components/PageContainer';

export const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const RootCauseAnalysis = () => {
    const [records, setRecords] = useState({});
    const [processing, setProcessing] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [fetching, setFetching] = useState(null);
    const [validating, setValidating] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [recordDetails, setRecordDetails] = useState({});
    const [openDrawer, setOpenDrawer] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    const { enqueueSnackbar } = useSnackbar();

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 100,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.rootCauseAnalysisUploadHistoryId) || 0) + 1}</p>;
            },
        },
        {
            field: 'id',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <IconButton size="small" color="primary" onClick={async () => await fetchRecord(params?.row)} title="More Details">
                        {fetching == params?.row?.rootCauseAnalysisUploadHistoryId ? <CircularProgress size={15} /> : <DescriptionOutlined />}
                    </IconButton>

                    <IconButton
                        size="small"
                        color="info"
                        onClick={() => {
                            setRecordDetails(params?.row);
                            setOpenDialog(true);
                        }}
                        title="Validate"
                    >
                        <VerifiedOutlined />
                    </IconButton>
                </div>
            ),
        },
        {
            field: 'institutionId',
            headerName: 'Institution',
            width: 250,
            valueGetter: (params) => params?.row?.institution?.name || '',
        },
        {
            field: 'rootCauseAnalysisPeriod',
            headerName: 'Period',
            width: 250,
            valueGetter: (params) => `${params?.row?.year || ''} ${params?.row?.quarter || ''}`,
        },
        { field: 'originalFileName', headerName: 'Filename', width: 200 },
        {
            field: 'processingStatus',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => getStatus(params?.row?.processingStatus),
        },

        { field: 'totalRecords', headerName: 'Total Records', width: 150 },

        {
            field: 'submissionDate',
            headerName: 'Submission Date',
            width: 200,
            valueGetter: (params) => moment(params?.row?.createdOn).format('DD MMMM, YYYY'),
        },
        {
            field: 'submissionTime',
            headerName: 'Submission Time',
            width: 200,
            valueGetter: (params) => moment(params?.row?.createdOn).format('hh:mm:ss A'),
        },
    ];

    // SUBMITTED, PROCESSING, PROCESSED
    const getStatus = (status = 'SUBMITTED') => {
        switch (status) {
            case 'SUBMITTED':
                return <Chip size="small" color="info" label={<small>Submitted</small>} />;
            case 'PROCESSING':
                return <Chip size="small" color="warning" label={<small>Processing</small>} />;
            case 'PROCESSED':
                return <Chip size="small" color="primary" label={<small>Processed</small>} />;
            default:
                return <Chip size="small" color="primary" label={<small>Processed</small>} />;
        }
    };

    const init = async () => {
        try {
            setProcessing(true);
            const res = await GetRootCauseAnalysisUploadForInstitution();
            setRecords(res?.resultObject || []);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const fetchRecord = async (record) => {
        try {
            setFetching(record.rootCauseAnalysisUploadHistoryId);
            const res = await GetRootCauseAnalysisUploadForInstitutionById(record.rootCauseAnalysisUploadHistoryId);
            setRecordDetails({ ...record, ...res?.resultObject, rootCauseAnalysis: res?.resultObject?.rootCauseAnalysis?.filter((r) => r.isSystemGenerated == false) || [] });
            setOpenDrawer(true);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setFetching(null);
        }
    };

    const exportData = async (year = '') => {
        try {
            setExporting(year || true);
            const resp = await GetRootCauseAnalysisUploadForInstitution(true, year);
            if (resp?.isSuccess) {
                enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
            } else {
                enqueueSnackbar(resp?.message || `An error occurred`, {
                    variant: 'error',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    const validateData = async (year = '') => {
        try {
            setValidating(true);
            const resp = await ValidateReturn(recordDetails.rootCauseAnalysisUploadHistoryId);

            if (resp?.isSuccess) {
                enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
                setOpenDialog(false);
                setRecordDetails(null);
            } else {
                enqueueSnackbar(resp?.message || `An error occurred`, {
                    variant: 'error',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setValidating(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer pageTitle="Root Cause Analysis" processing={processing}>
            <Box>
                <div className="paper" style={{ height: 550, width: '100%', textAlign: 'center' }}>
                    {records?.length > 0 && (
                        <div className="export-btn">
                            <Button disabled={exporting} onClick={async () => await exportData('')} variant="outlined" color="info" startIcon={<FileDownloadOutlined />}>
                                {exporting ? <CircularProgress color="inherit" size={15} /> : 'Export'}
                            </Button>
                        </div>
                    )}
                    <DataGrid
                        className="center-first-column"
                        rows={records || []}
                        getRowId={(row) => row.rootCauseAnalysisUploadHistoryId}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        style={{ fontSize: '12px' }}
                    />
                </div>
            </Box>

            <Drawer
                anchor="right"
                open={openDrawer}
                onClose={() => {
                    setOpenDrawer(false);
                    setRecordDetails(null);
                }}
            >
                <Grid container style={{ minWidth: '50vw' }}>
                    <Grid item xs={12} container className="bg-primary p-2 pl-1" alignItems={'center'}>
                        <Grid item xs={11}>
                            Root cause analysis Details
                        </Grid>
                        <Grid item xs={1} justifyContent="flex-end" container>
                            <IconButton
                                color="inherit"
                                onClick={() => {
                                    setOpenDrawer(false);
                                    setRecordDetails(null);
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} flex={1}>
                        <List className="upload-details">
                            <ListItem>
                                <b>Root Cause Analysis Period: </b> {recordDetails?.year || ''} {recordDetails?.quarter || ''}
                            </ListItem>
                            <ListItem>
                                <b>Institution: </b> {recordDetails?.institution?.name}
                            </ListItem>
                            <ListItem>
                                <b>Total Records: </b> {recordDetails?.originalFileName}
                            </ListItem>
                            <ListItem>
                                <b>Created On: </b> {recordDetails?.createdOn ? moment(recordDetails?.createdOn).format('Do MMMM, YYYY - hh:mm A') : ''}
                            </ListItem>
                            <ListItem className="mt-1">
                                <b>Root Cause Analysis ({recordDetails?.rootCauseAnalysis?.length || 0})</b>
                            </ListItem>
                        </List>

                        <Tabs value={tabValue} onChange={(_e, value) => setTabValue(value)}>
                            <Tab label={`Uploaded Root Cause (${recordDetails?.rootCauseAnalysis?.filter((r) => !r.isSystemGenerated).length})`} />
                            <Tab label={`System Generated Root Cause (${recordDetails?.rootCauseAnalysis?.filter((r) => r.isSystemGenerated).length})`} />
                        </Tabs>

                        <Box
                            sx={{
                                width: '100%',
                                borderTop: 1,
                                borderColor: 'divider',
                            }}
                        >
                            {recordDetails?.rootCauseAnalysis
                                ?.filter((r) => (tabValue == 0 ? !r.isSystemGenerated : r.isSystemGenerated))
                                ?.map((details, key) => (
                                    <Accordion key={key}>
                                        <AccordionSummary className="f-12">
                                            <span>
                                                <b>{key + 1}.</b> Identified Root Cause: {details?.identifiedRootCause}
                                            </span>
                                        </AccordionSummary>
                                        <AccordionDetails className="root-details f-12">
                                            <p>
                                                <b>Description: </b> {details?.description}
                                            </p>
                                            <p>
                                                <b>Planned Remedial Action: </b> {details?.plannedRemedialAction}
                                            </p>
                                            <p>
                                                <b>Status:</b> {details?.status}
                                            </p>
                                            <p>
                                                <b>Status of Remedial Action:</b> {details?.statusOfRemedialAction}
                                            </p>
                                            <p>
                                                <b>Subcategory: </b> {details?.subCategoryCode}
                                            </p>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                        </Box>
                    </Grid>
                </Grid>
            </Drawer>

            <Dialog open={openDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <h4>Validate Return</h4>
                    <Divider />
                </DialogTitle>

                <DialogContent>
                    <p>Generate report for root cause analysis for:</p>
                    <p className="mt-0">
                        <b>Institution:</b> {recordDetails?.institution?.name || ''}
                    </p>
                    <p className="mt-0">
                        <b>Period:</b> {recordDetails?.year || ''} {recordDetails?.quarter || ''}
                    </p>
                </DialogContent>
                <DialogActions style={{ padding: '0 1rem 1rem 0' }}>
                    <Button
                        disabled={validating}
                        onClick={() => {
                            setOpenDialog(false);
                            setRecordDetails(null);
                        }}
                        color="secondary"
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button disabled={validating} variant="contained" onClick={validateData}>
                        {validating ? <CircularProgress size="small" /> : 'Validate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default RootCauseAnalysis;
